/**
 * Sharing a song — the app's ONE share action, as a hook.
 *
 * WHY IT EXISTS. This was the body of `onShare` inside modals/AppModals, where the lyrics
 * dialog was the only thing that could share. Share is now on the catalogue cards too, and
 * a second copy of "build the URL, call the Web Share API, fall back to the clipboard,
 * toast the right sentence for the outcome" is exactly how two share buttons end up
 * behaving differently — one of them silently failing, or one of them omitting the caveat
 * about the link not resolving yet. So the body moved here and both callers use it.
 *
 * NO NEW SHARE MACHINERY. It calls the same `lib/share.songShareUrl` for the URL and the
 * same `lib/webShare.shareLink` for the system sheet and clipboard fallback that were
 * already there. This hook is a wiring point, not an implementation.
 *
 * THE TOASTS CARRY A CAVEAT, and it is the same one ShareDialog prints above its buttons:
 * the link does not resolve yet. `shareApi.getShared` refuses a song that is not public or
 * unlisted, and there is no server behind the URL in any case. Sending a link that opens
 * nothing is the mistake sharing makes easiest, so the sentence travels with the action
 * rather than living only in the Studio's dialog.
 *
 * AND IT IS THE ONE PLACE THE SHARE COUNT IS INCREMENTED, which is the reason the count can
 * be trusted at all: this hook is the app's single share action, so `recordShare` here counts
 * every share once and nothing else counts one. It is called only for an outcome that
 * actually left the app — 'shared' or 'copied'. A dismissed sheet shared nothing, and a
 * failed one shared nothing either; counting those would make the number a press counter.
 */
import { songShareUrl } from '../lib/share';
import { shareLink } from '../lib/webShare';
import { useAuthStore } from '../stores/authStore';
import { useSongsStore } from '../stores/songsStore';
import { useUiStore } from '../stores/uiStore';
import type { Song } from '../types/domain';

const sharedThisSession = new Set<string>();

export function resetSharedSongsThisSession(): void {
  sharedThisSession.clear();
}

/** Returns the share handler. Give it a song; it does the rest. */
export function useShareSong(onShared?: (song: Song) => void): (song: Song) => void {
  const showToast = useUiStore((s) => s.showToast);
  // One field per selector call, per Zustand v5's Object.is rule.
  const recordShare = useSongsStore((s) => s.recordShare);

  return (song: Song) => {
    if (song.status !== 'ready') {
      showToast('Only ready songs can be shared.');
      return;
    }
    if (song.visibility !== 'public') {
      showToast('Make this song discoverable before sharing it.');
      return;
    }

    void shareLink({
      url: songShareUrl(song.id),
      title: song.title,
      text: `${song.title} — made with Lyrixsa AI`,
    }).then((outcome) => {
      // A dismissed share sheet is a decision, not an error — see lib/webShare on why
      // reporting it teaches people the button is broken.
      if (outcome === 'dismissed') return;
      if (outcome !== 'failed') {
        const viewerId = useAuthStore.getState().user?.id ?? 'anonymous';
        const key = `${viewerId}:${song.id}`;
        if (!sharedThisSession.has(key)) {
          sharedThisSession.add(key);
          recordShare(song.id);
          onShared?.(song);
        }
      }
      showToast(
        outcome === 'failed'
          ? 'Could not share the link — check clipboard permissions'
          : outcome === 'copied'
            ? 'Link copied'
            : 'Link shared'
      );
    });
  };
}
